import React, { useCallback, useEffect, useState } from "react";
import styles from "./styles.module.scss";
import DashboardWidget from "../../../atoms/DashboardWidget";
import {
  Layers,
  Users,
  ListTodo,
  FileChartColumnIncreasing,
  Contact,
  FileClock,
  ClockAlert,
} from "lucide-react";
import GraphContainer from "./GraphContainer";
import { useSelector } from "react-redux";
import { getData } from "../../../../services";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import PieCharts from "../reports/GraphContainer/PieCharts";
import GraphContainerClient from "./GraphContainer";
import CustomDataGrid from "../../../organisms/CustomDatagrid";
import CommonFilter from "../../../atoms/CommonFilter";
import Pagination from "../../../molecules/Pagination";
import DateRangePickerShortCuts from "../../../organisms/dateRangePicker";
import { addHours, startOfDay, subDays } from "rsuite/esm/internals/utils/date";
import { getClientList, getEmployeeList } from "../../../../services/basicGets";
import NoContentCard from "../../../atoms/NoContentCard";
import { Tooltip } from "@mui/material";
import { formatHoursToHm } from "../../../../helpers/formatTime";
import TaskManagement from "../taskManagement";
import { formatTime } from "../../../../helpers/timeformathoursminsec";

const formatHours = (hours) => {
  if (typeof hours !== "number" || hours <= 0 || isNaN(hours)) return "0m";

  // Convert hours to total minutes
  const totalMinutes = hours * 60;
  // Extract whole hours
  const wholeHours = Math.floor(totalMinutes / 60);
  // Extract remaining minutes, rounding to nearest minute
  const minutes = Math.round(totalMinutes % 60);

  // Format output
  if (wholeHours > 0 && minutes > 0) {
    return `${wholeHours}h ${minutes}m`;
  } else if (wholeHours > 0) {
    return `${wholeHours}h`;
  } else {
    return `${minutes}m`;
  }
};

const ClientDashboard = () => {
  const navigate = useNavigate();

  const accessData = useSelector((state) => state.accessDetails?.accessData);
  const isAdmin = accessData?.role?.label === "Admin";
  const isClientmanagement =
    accessData?.access?.find(
      (module) => module.module === "Client Dashboard"
    ) || {};

  const canCreate = isAdmin || isClientmanagement?.is_create;
  const canEdit = isAdmin || isClientmanagement?.is_edit;
  const canDelete = isAdmin || isClientmanagement?.is_delete;
  const canExport = isAdmin || isClientmanagement?.is_export;

  const [cookies] = useCookies([]);
  const [tableData, setTableData] = useState([]);
  const [employeeTimeData, setEmployeeTimeData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0); // State for total records
  const [currentPage, setCurrentPage] = useState(1); // State for current page
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [clients, setClients] = useState([]);
  const [projectAnalysis, setProjectAnalysis] = useState([]);
  const [filters, setFilters] = useState({});
  const [isTimeTableLoading, setIsTimeTableLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false); // Add this to track initialization
  // const [isClientsLoaded, setIsClientsLoaded] = useState(false);

  const defaultToday = new Date();
  const [date, setDate] = useState({
    start_date: addHours(startOfDay(subDays(defaultToday, 14)), 12), // 14 days ago
    end_date: addHours(startOfDay(defaultToday), 12), // Today
  });

  const getDashboardData = useCallback(async () => {
    try {
      const res = await getData({
        endpoint: isAdmin
          ? "Dashboard/getAdminDashboardCount"
          : "Dashboard/getEmployeeDashboardCount",
        params: {
          start_date: date?.start_date,
          end_date: date?.end_date,
          emp_id: isAdmin ? null : cookies.euid,
          client_id: filters?.client ? filters?.client?.[0] : null,
          api_version: null,
        },
        token: cookies.e_t,
      });
      if (res) {
        setTableData(res);
      }
    } catch (error) {
      console.error(error);
    }
  }, [
    cookies.e_t,
    isAdmin,
    cookies.euid,
    cookies.buid,
    filters,
    date?.start_date,
    date?.end_date,
  ]);

  const getProjectAnalysis = useCallback(async () => {
    try {
      const res = await getData({
        endpoint: "Dashboard/getProjectAnalysis",
        params: {
          start_date: date?.start_date,
          end_date: date?.end_date,
          project_ids: JSON.stringify(filters?.project),
          employee_ids: JSON.stringify(filters?.employee),
          team_ids: JSON.stringify(filters?.team),
          employee_id: isAdmin ? null : cookies.euid,
          client_ids: filters?.client ? JSON.stringify(filters?.client) : null,
          businessSegment: JSON.stringify(filters?.business_segment),
        },
        token: cookies.e_t,
      });
      if (res) {
        setProjectAnalysis(res?.chart_data?.[0]?.data);
      }
    } catch (error) {
      console.error(error); // Use console.error for errors
    }
  }, [cookies.e_t, isAdmin, cookies.euid, cookies.buid, filters]);

  const getEmployeeTimeData = useCallback(async () => {
    // Only make API call if client is selected

    try {
      setIsTimeTableLoading(true);
      const res = await getData({
        endpoint: "Dashboard/getEmployeeLoggedTimeBasedOnClient",
        params: {
          start_date: date?.start_date,
          end_date: date?.end_date,
          client_id: filters?.client ? filters?.client?.[0] : null,
          page: currentPage,
          page_limit: itemsPerPage,
        },
        token: cookies.e_t,
      });
      if (res) {
        setEmployeeTimeData(res?.employee_client_time_summary || []);
        setTotalRecords(res.total_records || 0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsTimeTableLoading(false);
    }
  }, [cookies.e_t, filters, cookies.buid]);

  // Initialize clients and set first client as default
  useEffect(() => {
    const initializeClients = async () => {
      try {
        const clients = await getClientList({
          token: cookies.e_t,
        });

        if (clients?.data?.length > 0) {
          setClients(clients.data);
          // Set the first client as default filter
          //setFilters((prev) => ({ ...prev, client: [clients.data[0].value] }));
          setIsInitialized(true); // Mark as initialized
        }
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
    };

    if (cookies.e_t) {
      initializeClients();
    }
  }, [cookies.e_t, cookies.buid]);

  // Only call dashboard data API after initialization and when accessData is available
  useEffect(() => {
    // if (isClientsLoaded &&  accessData) {
    //   getDashboardData();
    // }
    if (accessData) {
      getDashboardData();
    }
  }, [getDashboardData, accessData, isInitialized]);

  // Only call these APIs after initialization
  useEffect(() => {
    // if (isClientsLoaded) {
    getEmployeeTimeData();
    getProjectAnalysis();
    // }
  }, [getEmployeeTimeData, getProjectAnalysis, isInitialized]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const filterOptions = [
    {
      id: "client",
      label: "Client",
      type: "single-select",
      options: Array.isArray(clients) ? clients : [],
      value: filters.client || [],
    },
  ];

  const timeTableColumns = [
    {
      field: "employeeName",
      headerName: "Employee Name",
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {params.value}
          </span>
        </Tooltip>
      ),
    },
    {
      field: "totalHours",
      headerName: "Logged Time",
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <span style={{ fontWeight: 500, color: "#6C5DD3" }}>
          {formatHours(params.value)}
        </span>
      ),
    },
  ];

  const CustomNoRowsOverlay = React.memo(() => {
    return (
      <>
        <NoContentCard title="No Logged Time Found" type="Task" />
      </>
    );
  });

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((perPage) => {
    setCurrentPage(1);
    setItemsPerPage(perPage);
  }, []);

  return (
    <>
      <div className="main-container">
        <div className="mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <div className="d-flex gap-2 align-items-center">
              <CommonFilter
                clientDash={true}
                filters={filterOptions}
                initialFilters={filters}
                onFilterChange={handleFilterChange}
              />
            </div>
            <DateRangePickerShortCuts
              clearPreselected={true}
              setDate={setDate}
            />
          </div>
        </div>
        <div
          className={`${styles.widget_container} ${
            isAdmin ? styles.admin : styles.nonAdmin
          }`}
        >
          <DashboardWidget
            title={"Total Projects"}
            count={tableData?.project_count || 0}
            icon={<Layers size={22} color="#6C5DD3" />}
            // onClick={() => navigate("/dashboard/projects")}
          />

          <DashboardWidget
            title={"Total Hours Logged"}
            count={tableData?.client_summary?.totalHoursLogged || 0}
            icon={<Layers size={22} color="#6C5DD3" />}
            // onClick={() => navigate("/dashboard/projects")}
          />

          <DashboardWidget
            title={"Total Tasks"}
            count={tableData?.client_summary?.totalTasks || 0}
            icon={<Layers size={22} color="#6C5DD3" />}
            // onClick={() => navigate("/dashboard/projects")}
          />

          <DashboardWidget
            title={"Completed Tasks"}
            count={tableData?.completed_task || 0}
            icon={<ListTodo size={22} color="#6C5DD3" />}
            onClick={() =>
              navigate(
                `/dashboard/tasks?status=["Completed"]&client=${JSON.stringify(
                  filters?.client || []
                )}`
              )
            }
          />

          <DashboardWidget
            title={"Pending Tasks"}
            count={tableData?.pending_task || 0}
            icon={<FileChartColumnIncreasing size={22} color="#6C5DD3" />}
            onClick={() =>
              navigate(
                `/dashboard/tasks?status=["New Task","In Progress"]&client=${JSON.stringify(
                  filters?.client || []
                )}`
              )
            }
          />

          <DashboardWidget
            title={"Delayed Tasks"}
            count={tableData?.delay_task_count || 0}
            icon={<FileClock size={22} color="#6C5DD3" />}
            onClick={() =>
              navigate(
                `/dashboard/tasks?delayed=["delayed"]&client=${JSON.stringify(
                  filters?.client || []
                )}`
              )
            }
          />

          <DashboardWidget
            title={"Overdue Tasks"}
            count={tableData?.overdue_task_count || 0}
            icon={<ClockAlert size={22} color="#6C5DD3" />}
            onClick={() =>
              navigate(
                `/dashboard/tasks?delayed=["duepast"]&client=${JSON.stringify(
                  filters?.client || []
                )}`
              )
            }
          />
        </div>

        <div className="col-12">
          <GraphContainerClient
            clientDash={true}
            client={filters?.client}
            dates={date}
            style={{ width: "100%", height: "400px" }}
          />
        </div>

        <div className="d-flex gap-4">
          <div
            className="w-100 rounded-3 shadow"
            style={{ height: "442px", marginTop: "32px" }}
          >
            <CustomDataGrid
              columns={timeTableColumns}
              CustomNoRowsOverlay={CustomNoRowsOverlay}
              rows={employeeTimeData || []}
              getRowId={(row) => row.employeeName}
              isLoadingStateProvided={true}
              isLoading={isTimeTableLoading}
              autoHeight={true}
              disableSelectionOnClick={true}
            />
          </div>

          <div className="col-12 w-50">
            <PieCharts
              id="projects-pie"
              title="Projects Analysis"
              data={projectAnalysis}
              style={{ width: "100%", height: "400px" }}
            />
          </div>
        </div>

        {totalRecords > 10 && (
          <Pagination
            itemsPerPage={itemsPerPage}
            totalItems={totalRecords}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        )}

        <TaskManagement clientshow={true} clientdash={filters?.client} />
      </div>
    </>
  );
};

export default ClientDashboard;
